// Authentication state hook. Backed by /api/auth/me which always returns 200
// with { authenticated, usuario, contrasenaCompartida }. We map that to a three-state machine so the
// router can show a loading screen while we don't yet know if the cookie is valid.
//
// Cuentas personales (docs/cuentas-personales.md): la entrada es en dos pasos. `login(password, usuario)`
// con usuario devuelve un reto; `loginCodigo(reto, codigo)` abre la sesión. Sin usuario es la contraseña
// compartida de antes, que solo vale hasta que Pau termina su alta.

import { useCallback, useEffect, useState } from 'react';
import { api, ApiHttpError } from '../lib/api';

export type AuthState = 'unknown' | 'authed' | 'unauthed';

export interface Yo {
  nombre: string;
  rol: 'titular' | 'socio';
  legado: boolean;
  /** 06-10 — su cuenta (null = contraseña compartida o programa del servidor): qué agentes son suyos en la oficina. */
  cuentaId?: string | null;
}

interface MeResponse {
  authenticated: boolean;
  usuario?: Yo | null;
  contrasenaCompartida?: boolean;
}

export type ResultadoLogin = { segundoPaso: false } | { segundoPaso: true; reto: string };

function comoApiError(err: unknown): ApiHttpError {
  if (err instanceof ApiHttpError) return err;
  return new ApiHttpError(0, 'NETWORK_ERROR', 'Could not reach the server.');
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>('unknown');
  const [yo, setYo] = useState<Yo | null>(null);
  const [contrasenaCompartida, setContrasenaCompartida] = useState(true);

  const refrescar = useCallback(async () => {
    try {
      const r = await api<MeResponse>('/api/auth/me');
      setYo(r.usuario ?? null);
      setContrasenaCompartida(r.contrasenaCompartida ?? true);
      setAuthState(r.authenticated ? 'authed' : 'unauthed');
    } catch {
      setAuthState('unauthed');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api<MeResponse>('/api/auth/me')
      .then((r) => {
        if (cancelled) return;
        setYo(r.usuario ?? null);
        setContrasenaCompartida(r.contrasenaCompartida ?? true);
        setAuthState(r.authenticated ? 'authed' : 'unauthed');
      })
      .catch(() => {
        if (cancelled) return;
        setAuthState('unauthed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (password: string, usuario?: string): Promise<ResultadoLogin> => {
      try {
        const r = await api<{ ok: true; segundoPaso?: boolean; reto?: string }>('/api/auth/login', {
          method: 'POST',
          body: usuario ? { usuario, password } : { password },
        });
        if (r.segundoPaso && r.reto) return { segundoPaso: true, reto: r.reto };
        await refrescar();
        return { segundoPaso: false };
      } catch (err) {
        throw comoApiError(err);
      }
    },
    [refrescar],
  );

  const loginCodigo = useCallback(
    async (reto: string, codigo: string): Promise<{ recuperacionesQuedan: number | null }> => {
      try {
        const r = await api<{ ok: true; recuperacionesQuedan: number | null }>('/api/auth/login/codigo', {
          method: 'POST',
          body: { reto, codigo },
        });
        await refrescar();
        return { recuperacionesQuedan: r.recuperacionesQuedan };
      } catch (err) {
        throw comoApiError(err);
      }
    },
    [refrescar],
  );

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>('/api/auth/logout', { method: 'POST' });
    } catch {
      // Even if the request fails (network), clearing local state is fine —
      // the cookie is HttpOnly so we can't clear it ourselves; the server
      // call is the only way. We still reflect "logged out" in the UI.
    }
    setYo(null);
    setAuthState('unauthed');
  }, []);

  return { authState, yo, contrasenaCompartida, login, loginCodigo, logout, refrescar, setAuthState };
}
